import { apyToAprPercent } from '../../../utils/parsing'
import { YieldFetcher } from '../../types'
import { fetchDefiLlamaApy } from './defillama'

/**
 * f(x) Protocol fxSAVE — the ERC-4626 auto-compounder over `fxSP`, the
 * f(x) Stability Pool share. Depositors hold fxUSD-denominated stability-pool
 * exposure and earn the pool's rebalance/liquidation revenue, which is
 * harvested back into fxSP so the fxSAVE share price appreciates (1.1005
 * fxSP/share at integration, 2026-08-09). Exit is a plain instant 4626 redeem
 * — verified on-chain the same day: `maxDeposit = uint.max`,
 * `maxRedeem == balanceOf` for a live holder, and `previewRedeem` equals
 * `convertToAssets` (no exit fee, no cooldown).
 *
 * NOTE the underlying is **fxSP, not fxUSD** — `asset()` returns
 * `0x65c9a641…` and fxSP is itself appreciating ($1.0101 vs fxUSD's $1.0000
 * at integration), so `totalAssets` must be priced as fxSP. The token-list
 * `props.savings.underlying: 'fxUSD'` on the fxSAVE row is a deliberate
 * economic-underlying pin for the stablecoin classifier and is NOT the 4626
 * asset.
 *
 * Rate source: Aladdin DAO's own `concentrator_aToken_tvl_apy` endpoint
 * (`data.fxSave.apy`, a percent APY; its `tvl` reconciles with
 * `totalAssets × fxSP price` to the dollar). Fallback is the DefiLlama
 * `concentrator` fxSAVE pool — which is that same endpoint restated (identical
 * APY, TVL matching to four decimals), so it is a transport fallback rather
 * than an independent source.
 *
 * The published figure is an **APY**: converting 5.24 % with the package's
 * per-second helper gives 5.11 %, exactly what DefiLlama's separate
 * `fx-protocol` FXUSDSTABILITYPOOLV2.0 pool reports for the same underlying
 * yield one layer down. Trailing realised share-price growth was higher at
 * integration (5.45 % APR over 7d, 6.41 % over 30d) because the published
 * number is forward-looking, not a lookback.
 */

const ALADDIN_APY_URL =
  'https://api.aladdin.club/api1/concentrator_aToken_tvl_apy'
const FXSAVE_DEFILLAMA_POOL = 'ee0b7069-f8f3-4aa2-a415-728f13e6cc3d'

export const FXSAVE_KEY = 'FXSAVE'
/** Verified token-list assetGroup (Ethereum, the only deployment). */
export const FXSAVE_GROUP_KEY = 'f(x) USD Saving::fxSAVE'

const fetchAladdinApyPercent = async (): Promise<number> => {
  const res = await fetch(ALADDIN_APY_URL, {
    headers: { accept: 'application/json' },
    signal: AbortSignal.timeout(8000),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const apy = Number((await res.json())?.data?.fxSave?.apy)
  if (Number.isFinite(apy) && apy > 0) return apy
  throw new Error('apy missing')
}

export const fxSaveFetcher: YieldFetcher = {
  label: 'FXSAVE',
  fetch: async () => {
    const apy = await fetchAladdinApyPercent().catch(() =>
      fetchDefiLlamaApy(FXSAVE_DEFILLAMA_POOL),
    )
    const apr = apyToAprPercent(apy)
    return { [FXSAVE_KEY]: apr, [FXSAVE_GROUP_KEY]: apr }
  },
}
